import React, { useState, useEffect } from 'react';
import { Moon, Sun, Laptop, Check, Sparkles } from 'lucide-react';

export type Theme = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'app_theme';

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (saved && ['dark', 'light', 'system'].includes(saved)) {
    return saved;
  }
  return 'dark'; // Default theme
}

export function applyThemeToElement(theme: Theme) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;

  let isDark = theme === 'dark';
  if (theme === 'system') {
    isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  if (isDark) {
    root.classList.add('dark');
    root.classList.remove('light');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }

  root.setAttribute('data-theme', theme);
}

// Initial application on script evaluation
if (typeof window !== 'undefined') {
  applyThemeToElement(getStoredTheme());
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch (err) {
      console.warn('Unable to persist theme to localStorage:', err);
    }
    applyThemeToElement(newTheme);
  };

  useEffect(() => {
    applyThemeToElement(theme);

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyThemeToElement('system');

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } else if ('addListener' in mediaQuery) {
        (mediaQuery as any).addListener(handleChange);
        return () => (mediaQuery as any).removeListener(handleChange);
      }
    }
  }, [theme]);

  return { theme, setTheme };
}

interface ThemeToggleProps {
  className?: string;
  variant?: 'grid' | 'pills';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', variant = 'grid' }) => {
  const { theme, setTheme } = useTheme();

  const options: { id: Theme; label: string; description: string; icon: React.FC<{ className?: string }> }[] = [
    {
      id: 'dark',
      label: 'Dark Mode',
      description: 'High-contrast dark interface tailored for evening play and pitch viewing',
      icon: Moon,
    },
    {
      id: 'light',
      label: 'Light Mode',
      description: 'Clean bright layout with optimal legibility under direct sunlight',
      icon: Sun,
    },
    {
      id: 'system',
      label: 'System Sync',
      description: 'Automatically synchronizes with your device light/dark preferences',
      icon: Laptop,
    },
  ];

  if (variant === 'pills') {
    return (
      <div className={`inline-flex p-1 rounded-2xl bg-slate-900 border border-slate-800 ${className}`}>
        {options.map((opt) => {
          const Icon = opt.icon;
          const isActive = theme === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setTheme(opt.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-black uppercase transition ${
                isActive
                  ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{opt.id}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-black text-sm uppercase text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            Theme Preference
          </h4>
          <p className="text-xs text-slate-400 mt-0.5 font-medium">
            Choose your preferred color theme for Turf Terminal
          </p>
        </div>
        <span className="text-[10px] font-mono font-black uppercase px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
          Active: {theme}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const isActive = theme === opt.id;

          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setTheme(opt.id)}
              className={`relative text-left p-4 rounded-2xl border-2 transition-all flex flex-col justify-between ${
                isActive
                  ? 'bg-slate-900 border-emerald-500 ring-2 ring-emerald-500/20 shadow-lg shadow-emerald-500/10'
                  : 'bg-slate-950/80 hover:bg-slate-900 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-start justify-between w-full mb-3">
                <div
                  className={`p-2.5 rounded-xl ${
                    isActive ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  <Icon className="w-5 h-5 stroke-[2.5]" />
                </div>
                {isActive && (
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-black">
                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                  </span>
                )}
              </div>

              <div>
                <span className={`font-black text-xs uppercase block ${isActive ? 'text-emerald-400' : 'text-white'}`}>
                  {opt.label}
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-snug font-medium">
                  {opt.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
